import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MOCK_MODE } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { Icon } from '../components/icons';
import { Field } from '../components/ui';

/**
 * S1 — Giriş. Birincil yol SelfPortal SSO; break-glass iki adımlıdır:
 *   1) e-posta + parola → sunucu 2FA bileti döner
 *   2) bilet + TOTP → oturum açılır
 * Mock modda aynı akış demo koduyla (123456) çalışır.
 */
export default function LoginPage() {
  const { user, loading, loginSso, loginPassword, verify2fa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || '/inbox';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ticket, setTicket] = useState<string | null>(null);
  const [totp, setTotp] = useState('');
  const [role, setRole] = useState<'owner' | 'agent'>('owner');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'sso' | 'password' | '2fa' | null>(null);

  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  async function handleSso() {
    setError(null);
    setBusy('sso');
    try {
      if (MOCK_MODE) {
        await loginSso(role);
        navigate(from, { replace: true });
      } else {
        // Tam sayfa yönlendirme; dönüşte /api/auth/me oturumu görür.
        window.location.assign('/api/auth/sso/start');
      }
    } catch (cause) {
      setError(errorMessage(cause));
      setBusy(null);
    }
  }

  async function handlePassword(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy('password');
    try {
      const result = await loginPassword({ email, password });
      if (result.twoFactorRequired) {
        setTicket(result.ticket);
        setTotp('');
      } else {
        navigate(from, { replace: true });
      }
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  async function handleVerify(event: FormEvent) {
    event.preventDefault();
    if (!ticket) return;
    setError(null);
    setBusy('2fa');
    try {
      await verify2fa({ ticket, code: totp });
      navigate(from, { replace: true });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-paper px-4 py-10">
      <div className="w-full max-w-[380px]">
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-[4px] border border-signal text-[12px] font-semibold text-signal">
            DM
          </span>
          <div>
            <div className="text-[14px] font-semibold leading-5">Destek Merkezi</div>
            <div className="text-[11px] leading-4 text-muted">softwareeni · destek operasyonu</div>
          </div>
        </div>

        <div className="panel p-5">
          <h1 className="text-[16px]">Giriş</h1>
          <p className="mt-1 text-[12px] text-muted">
            Operasyon paneline yalnızca yetkili ekip erişebilir. Oturum çerezi HttpOnly ve SameSite=Lax olarak saklanır.
          </p>

          {MOCK_MODE ? (
            <div className="mt-3 flex items-center gap-2">
              <span className="badge badge-warn">MOCK</span>
              <span className="text-[11px] text-muted">Demo TOTP: 123456 · rol seçilebilir</span>
            </div>
          ) : null}

          <div className="mt-4 space-y-3">
            <button type="button" className="btn btn-primary w-full" onClick={() => void handleSso()} disabled={busy !== null}>
              <Icon name="shield" size={14} />
              {busy === 'sso' ? 'Yönlendiriliyor…' : 'SelfPortal ile giriş'}
            </button>

            {MOCK_MODE ? (
              <div role="group" aria-label="Demo rolü (giriş düğmesi SelfPortal)" className="flex items-center gap-1">
                {(['owner', 'agent'] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={role === value}
                    className={
                      'btn btn-sm flex-1 ' +
                      (role === value ? 'border-signal bg-signal-soft text-signal' : 'border-transparent text-muted')
                    }
                    onClick={() => setRole(value)}
                  >
                    {value === 'owner' ? 'Sahip' : 'Ajan'}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {ticket ? (
            <form className="mt-4 space-y-3" onSubmit={(event) => void handleVerify(event)}>
              <div className="rounded-[4px] border border-line bg-paper px-3 py-2 text-[12px] text-ink-2">
                Parola doğrulandı. İki adımlı doğrulama için authenticator uygulamasındaki 6 haneli kodu girin.
              </div>
              <Field label="Doğrulama kodu (TOTP)" htmlFor="login-totp" hint={MOCK_MODE ? 'Demo kod: 123456' : '6 haneli kod'}>
                <input
                  id="login-totp"
                  className="input mono tracking-[0.2em]"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={totp}
                  onChange={(event) => setTotp(event.target.value.replace(/[^0-9]/g, ''))}
                  aria-invalid={error ? true : undefined}
                  autoFocus
                  required
                />
              </Field>

              {error ? (
                <div className="toast toast-error" role="alert">
                  <Icon name="warning" size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}

              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn flex-1"
                  onClick={() => {
                    setTicket(null);
                    setError(null);
                  }}
                >
                  Geri
                </button>
                <button type="submit" className="btn btn-primary flex-1" disabled={totp.length !== 6 || busy !== null}>
                  {busy === '2fa' ? 'Doğrulanıyor…' : 'Doğrula'}
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="my-4 flex items-center gap-2">
                <span className="h-px flex-1 bg-line" />
                <span className="text-[11px] text-muted">veya break-glass</span>
                <span className="h-px flex-1 bg-line" />
              </div>

              <form className="space-y-3" onSubmit={(event) => void handlePassword(event)}>
                <Field label="E-posta" htmlFor="login-email">
                  <input
                    id="login-email"
                    className="input"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="ad@softwareeni.com"
                    required
                  />
                </Field>
                <Field label="Şifre" htmlFor="login-password">
                  <input
                    id="login-password"
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </Field>

                {error ? (
                  <div className="toast toast-error" role="alert">
                    <Icon name="warning" size={14} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                ) : null}

                <button type="submit" className="btn w-full" disabled={busy !== null}>
                  {busy === 'password' ? 'Kontrol ediliyor…' : 'Devam et'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-3 text-[11px] leading-4 text-muted">
          Erişim sorununda sahibe ulaşın. Tüm giriş denemeleri audit kaydına yazılır.
        </p>
      </div>
    </main>
  );
}
